import { useEffect, useRef } from 'react';
import { Member, useScore } from '../../../context/ScoreContext';

interface Pair {
  angel: Member;
  target: Member;
}

const PINK = '#c06b9a';
const GOLD = '#d4a843';
const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_HUD  = "'Orbitron', monospace";

// ── Avatar card ───────────────────────────────────────────────────────────────
function AvatarCard({
  member,
  label,
  revealed = true,
  size = 'md',
}: {
  member: Member | null;
  label: string;
  revealed?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const accent = member?.team === 'black' ? GOLD : '#e0e0e0';
  const wh = size === 'lg' ? 'w-24 h-24' : size === 'md' ? 'w-16 h-16' : 'w-12 h-12';
  const textSize = size === 'lg' ? 'text-3xl' : size === 'md' ? 'text-2xl' : 'text-xl';

  if (!revealed || !member) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div
          className={`${wh} rounded-full flex items-center justify-center relative`}
          style={{
            background: 'radial-gradient(circle, rgba(192,107,154,0.2), rgba(192,107,154,0.05))',
            border: '2px dashed rgba(192,107,154,0.5)',
            boxShadow: '0 0 24px rgba(192,107,154,0.3)',
            animation: 'mysteryPulse 2s ease-in-out infinite',
          }}
        >
          <i className="ri-question-mark text-2xl" style={{ color: 'rgba(192,107,154,0.6)' }} />
        </div>
        <span className="text-xs font-black" style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_DECO }}>???</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(192,107,154,0.12)', color: 'rgba(192,107,154,0.6)', fontFamily: FONT_BODY }}>{label}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2" style={{ animation: 'cardReveal 0.6s cubic-bezier(0.34,1.56,0.64,1) both' }}>
      {member.avatarUrl ? (
        <img src={member.avatarUrl} alt={member.name}
          className={`${wh} rounded-full object-cover object-top`}
          style={{ border: `2px solid ${accent}70`, boxShadow: `0 0 20px ${accent}40` }} />
      ) : (
        <div
          className={`${wh} rounded-full flex items-center justify-center ${textSize} font-black`}
          style={{
            background: `radial-gradient(circle at 35% 35%, ${accent}30, ${accent}08)`,
            color: accent,
            border: `2px solid ${accent}60`,
            boxShadow: `0 0 20px ${accent}35`,
          }}
        >
          {member.name.charAt(0).toUpperCase()}
        </div>
      )}
      <span className="text-sm font-black text-white" style={{ fontFamily: FONT_BODY, textShadow: `0 0 10px ${accent}60` }}>
        {member.name}
      </span>
      <span
        className="text-[9px] px-2 py-0.5 rounded-full font-black"
        style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40`, fontFamily: FONT_BODY }}
      >
        {label}
      </span>
    </div>
  );
}

// ── Burst particles ───────────────────────────────────────────────────────────
function BurstParticles({ trigger }: { trigger: number }) {
  const particles = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    angle: (i / 24) * 360 + Math.random() * 15,
    dist: 70 + Math.random() * 80,
    size: 5 + Math.random() * 5,
    color: [PINK, GOLD, '#f5c0de', '#fff', '#e8a0cc', GOLD][i % 6],
    delay: Math.random() * 0.12,
    shape: i % 3 === 0 ? '♥' : i % 3 === 1 ? '✦' : '●',
  }));

  return (
    <div key={trigger} className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 5 }}>
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute top-1/2 left-1/2 text-xs font-black select-none"
          style={{
            color: p.color,
            fontSize: `${p.size}px`,
            textShadow: `0 0 8px ${p.color}`,
            animation: `burstP${p.id % 4} 0.85s ${p.delay}s ease-out both`,
            '--bx': `${Math.cos((p.angle * Math.PI) / 180) * p.dist}px`,
            '--by': `${Math.sin((p.angle * Math.PI) / 180) * p.dist}px`,
          } as React.CSSProperties}
        >{p.shape}</div>
      ))}
    </div>
  );
}

// ── Floating hearts background ────────────────────────────────────────────────
function FloatingHearts() {
  const emojis = ['♥', '✦', '♥', '✦', '♥', '✦'];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {emojis.map((e, i) => (
        <div
          key={i}
          className="absolute text-lg select-none"
          style={{
            left: `${8 + i * 16}%`,
            bottom: '-8%',
            color: [PINK, GOLD, '#f5c0de', PINK, GOLD, '#e8a0cc'][i],
            opacity: 0.4,
            animation: `angelFloat ${4.5 + i * 0.7}s ${i * 0.55}s ease-in-out infinite`,
            textShadow: `0 0 12px ${[PINK, GOLD, '#f5c0de', PINK, GOLD, '#e8a0cc'][i]}`,
          }}
        >{e}</div>
      ))}
    </div>
  );
}

// ── Waiting indicator (for non-judge viewers) ─────────────────────────────────
function WaitingHint() {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-full border"
      style={{
        background: 'rgba(192,107,154,0.06)',
        borderColor: 'rgba(192,107,154,0.2)',
        animation: 'tapHint 2s ease-in-out infinite',
      }}>
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: PINK }} />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: PINK }} />
      </span>
      <span className="text-xs font-black" style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_BODY }}>等待裁判操作…</span>
    </div>
  );
}

// ── Intro Screen ──────────────────────────────────────────────────────────────
function IntroScreen({ pairCount, isJudge, onAdvance }: { pairCount: number; isJudge: boolean; onAdvance: () => void }) {
  return (
    <div className="relative z-10 flex flex-col items-center gap-5 px-8 text-center"
      style={{ animation: 'phaseIn 0.7s cubic-bezier(0.34,1.56,0.64,1)' }}>
      <div className="relative">
        <div className="w-28 h-28 rounded-full flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle at 35% 35%, rgba(192,107,154,0.35), rgba(192,107,154,0.06))',
            border: '2px solid rgba(192,107,154,0.5)',
            boxShadow: '0 0 50px rgba(192,107,154,0.5), 0 0 100px rgba(192,107,154,0.2)',
            animation: 'angelGlow 2s ease-in-out infinite',
          }}>
          <i className="ri-spy-line text-5xl" style={{ color: PINK, filter: `drop-shadow(0 0 18px ${PINK})` }} />
        </div>
        <div className="absolute inset-0 rounded-full"
          style={{ border: '1px dashed rgba(192,107,154,0.4)', animation: 'angelOrbit 5s linear infinite' }} />
        <div className="absolute -inset-4 rounded-full"
          style={{ border: '1px dashed rgba(192,107,154,0.15)', animation: 'angelOrbit 9s linear infinite reverse' }} />
      </div>

      <div>
        <p className="text-[11px] tracking-[0.65em] uppercase mb-2" style={{ color: 'rgba(192,107,154,0.55)', fontFamily: FONT_DECO }}>✦ Secret Angel ✦</p>
        <h2 className="font-black text-3xl text-white"
          style={{ fontFamily: FONT_DECO, textShadow: '0 0 30px rgba(192,107,154,0.8), 0 0 80px rgba(192,107,154,0.3)', letterSpacing: '0.08em' }}>
          小天使揭曉儀式
        </h2>
      </div>

      <p className="text-sm" style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_BODY }}>
        共 {pairCount} 對配對等待揭曉
      </p>

      <div className="flex flex-col items-center gap-2 mt-4">
        {isJudge ? (
          <button
            onClick={onAdvance}
            className="flex items-center gap-1.5 px-5 py-3 rounded-2xl border cursor-pointer whitespace-nowrap"
            style={{
              background: 'rgba(192,107,154,0.15)',
              borderColor: 'rgba(192,107,154,0.5)',
              boxShadow: '0 0 20px rgba(192,107,154,0.3)',
              animation: 'tapHint 1.5s ease-in-out infinite',
            }}>
            <i className="ri-play-fill text-lg" style={{ color: PINK }} />
            <span className="text-sm font-black" style={{ color: PINK, fontFamily: FONT_DECO }}>開始揭曉</span>
          </button>
        ) : (
          <WaitingHint />
        )}
      </div>
    </div>
  );
}

// ── Reveal Screen ─────────────────────────────────────────────────────────────
function RevealScreen({
  pair,
  revealed,
  index,
  total,
  isJudge,
  onAdvance,
}: {
  pair: Pair;
  revealed: boolean;
  index: number;
  total: number;
  isJudge: boolean;
  onAdvance: () => void;
}) {
  const burstKey = revealed ? 1 : 0;

  return (
    <div className="relative z-10 w-full max-w-sm px-5 flex flex-col items-center gap-6"
      style={{ animation: 'phaseIn 0.5s cubic-bezier(0.34,1.56,0.64,1)' }}>

      {/* Progress */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-black tabular-nums" style={{ color: 'rgba(192,107,154,0.6)', fontFamily: FONT_HUD }}>
          {index + 1}
        </span>
        <div className="flex gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className="h-1 rounded-full transition-all duration-400"
              style={{
                width: i === index ? '20px' : '6px',
                background: i < index ? PINK : i === index ? PINK : 'rgba(192,107,154,0.2)',
                boxShadow: i === index ? `0 0 6px ${PINK}` : 'none',
              }} />
          ))}
        </div>
        <span className="text-xs font-black tabular-nums" style={{ color: 'rgba(192,107,154,0.4)', fontFamily: FONT_HUD }}>
          {total}
        </span>
      </div>

      {/* Question */}
      <p className="text-center text-sm font-black"
        style={{ color: 'rgba(192,107,154,0.7)', fontFamily: FONT_BODY }}>
        {revealed
          ? `${pair.angel.name} 是 ${pair.target.name} 的小天使！`
          : `${pair.target.name} 的小天使是誰？`}
      </p>

      {/* 合體畫面：Both cards */}
      <div className="relative w-full">
        {revealed && <BurstParticles trigger={burstKey} />}

        <div className="flex items-center justify-center gap-4 relative">
          {/* Target (小主人) */}
          <div key={`target-${index}`} style={{ animation: 'slideInLeft 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <AvatarCard member={pair.target} label="小主人" size="lg" />
          </div>

          {/* Connector */}
          <div className="flex flex-col items-center gap-1 shrink-0 z-10">
            <div className="w-px h-6"
              style={{ background: 'linear-gradient(180deg, transparent, rgba(192,107,154,0.5))' }} />
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: revealed
                  ? 'radial-gradient(circle, rgba(192,107,154,0.4), rgba(192,107,154,0.08))'
                  : 'radial-gradient(circle, rgba(192,107,154,0.15), transparent)',
                border: `1.5px solid rgba(192,107,154,${revealed ? 0.7 : 0.3})`,
                boxShadow: revealed ? '0 0 20px rgba(192,107,154,0.6)' : 'none',
                transition: 'all 0.4s ease',
              }}>
              <i className={`${revealed ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}
                style={{
                  color: PINK,
                  animation: revealed ? 'heartBeat 0.6s ease-in-out 0.3s' : 'none',
                }} />
            </div>
            <div className="w-px h-6"
              style={{ background: 'linear-gradient(180deg, rgba(192,107,154,0.5), transparent)' }} />
          </div>

          {/* Angel (小天使) */}
          <div key={`angel-${index}-${revealed}`}>
            <AvatarCard member={pair.angel} label="小天使" size="lg" revealed={revealed} />
          </div>
        </div>

        {/* Revealed: glow connection line */}
        {revealed && (
          <div className="absolute top-1/2 left-1/4 right-1/4 h-px -translate-y-1/2 pointer-events-none"
            style={{
              background: `linear-gradient(90deg, transparent, ${PINK}60, transparent)`,
              animation: 'connectorGlow 0.8s ease-out both',
            }} />
        )}
      </div>

      {/* Action hint / button */}
      {isJudge ? (
        <button
          onClick={onAdvance}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full border cursor-pointer whitespace-nowrap"
          style={{
            background: 'rgba(192,107,154,0.12)',
            borderColor: 'rgba(192,107,154,0.4)',
            animation: 'tapHint 1.5s ease-in-out infinite',
          }}>
          <i className={revealed ? 'ri-arrow-right-line' : 'ri-eye-line'} style={{ color: PINK }} />
          <span className="text-xs font-black" style={{ color: PINK, fontFamily: FONT_BODY }}>
            {revealed ? '下一位' : '揭曉小天使'}
          </span>
        </button>
      ) : (
        <WaitingHint />
      )}
    </div>
  );
}

// ── Done Screen ───────────────────────────────────────────────────────────────
function DoneScreen({ pairs, isJudge, onClose }: { pairs: Pair[]; isJudge: boolean; onClose: () => void }) {
  return (
    <div className="relative z-10 w-full max-w-sm px-4 pb-4 flex flex-col gap-3 max-h-[90dvh] overflow-y-auto"
      style={{ animation: 'phaseIn 0.6s cubic-bezier(0.34,1.56,0.64,1)' }}>
      <div className="text-center py-4">
        <p className="text-[11px] tracking-[0.55em] uppercase mb-1" style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_DECO }}>✦ 揭曉完成 ✦</p>
        <p className="text-white text-xl font-black" style={{ fontFamily: FONT_DECO, textShadow: '0 0 20px rgba(192,107,154,0.6)' }}>小天使全部揭曉！</p>
      </div>

      <div className="space-y-2">
        {pairs.map((p, i) => {
          const angelColor = p.angel.team === 'black' ? GOLD : '#e0e0e0';
          const targetColor = p.target.team === 'black' ? GOLD : '#e0e0e0';
          return (
            <div key={i} className="rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(160deg, rgba(192,107,154,0.12), rgba(212,168,67,0.05), rgba(8,4,14,0.95))',
                border: '1.5px solid rgba(192,107,154,0.3)',
                animation: `phaseIn 0.5s ${i * 0.08}s both`,
              }}>
              <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.7), transparent)' }} />
              <div className="px-4 py-3 flex items-center gap-3">
                {/* Target */}
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  {p.target.avatarUrl ? (
                    <img src={p.target.avatarUrl} alt={p.target.name}
                      className="w-10 h-10 rounded-full object-cover object-top shrink-0"
                      style={{ border: `1.5px solid ${targetColor}50` }} />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-black shrink-0"
                      style={{ background: `${targetColor}20`, color: targetColor, border: `1.5px solid ${targetColor}40` }}>
                      {p.target.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-xs font-black truncate" style={{ fontFamily: FONT_BODY }}>{p.target.name}</p>
                    <p className="text-[9px]" style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_BODY }}>小主人</p>
                  </div>
                </div>

                <i className="ri-heart-fill text-sm shrink-0" style={{ color: PINK }} />

                {/* Angel */}
                <div className="flex-1 flex items-center gap-2 justify-end min-w-0">
                  <div className="min-w-0 text-right">
                    <p className="text-white text-xs font-black truncate" style={{ fontFamily: FONT_BODY, textShadow: '0 0 8px rgba(192,107,154,0.5)' }}>{p.angel.name}</p>
                    <p className="text-[9px]" style={{ color: PINK, fontFamily: FONT_BODY }}>💝 小天使</p>
                  </div>
                  {p.angel.avatarUrl ? (
                    <img src={p.angel.avatarUrl} alt={p.angel.name}
                      className="w-10 h-10 rounded-full object-cover object-top shrink-0"
                      style={{ border: `1.5px solid ${PINK}60`, boxShadow: `0 0 10px ${PINK}30` }} />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-black shrink-0"
                      style={{ background: 'rgba(192,107,154,0.2)', color: PINK, border: `1.5px solid ${PINK}50` }}>
                      {p.angel.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={e => { e.stopPropagation(); onClose(); }}
        className="w-full py-3.5 rounded-xl font-black text-sm cursor-pointer transition-all whitespace-nowrap"
        style={{
          background: isJudge
            ? 'linear-gradient(135deg, rgba(192,107,154,0.7), rgba(192,107,154,0.4))'
            : 'rgba(192,107,154,0.15)',
          color: 'white',
          border: '1px solid rgba(192,107,154,0.5)',
          fontFamily: FONT_DECO,
        }}
      >
        {isJudge ? '結束揭曉儀式（所有人關閉）' : '關閉畫面'}
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
interface Props {
  isJudge: boolean;
  onClose: () => void;
  onOpenJudgePanel: () => void;
}

export default function SecretAngelRevealCeremony({ isJudge, onClose }: Props) {
  const { scheduleState, members, setCeremonyProgress, setCeremonyActive } = useScore();
  const { secretPairings, ceremonyPhase, ceremonyIndex, ceremonyRevealed } = scheduleState;

  const pairs: Pair[] = Object.entries(secretPairings)
    .map(([angelId, targetId]) => ({
      angel: members.find(m => m.id === angelId),
      target: members.find(m => m.id === targetId),
    }))
    .filter((p): p is Pair => !!p.angel && !!p.target);

  const currentPair = pairs[ceremonyIndex] ?? null;

  const handleAdvance = () => {
    if (!isJudge) return;
    if (ceremonyPhase === 'intro') {
      setCeremonyProgress('reveal', 0, false);
    } else if (ceremonyPhase === 'reveal') {
      if (!ceremonyRevealed) {
        setCeremonyProgress('reveal', ceremonyIndex, true);
      } else if (ceremonyIndex < pairs.length - 1) {
        setCeremonyProgress('reveal', ceremonyIndex + 1, false);
      } else {
        setCeremonyProgress('done', ceremonyIndex, true);
      }
    }
  };

  const handleClose = () => {
    if (isJudge) {
      setCeremonyActive(false);
    } else {
      onClose();
    }
  };

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (pairs.length === 0) {
    return (
      <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center"
        style={{ background: 'radial-gradient(ellipse at 50% 40%, #1e0620 0%, #06020f 100%)' }}>
        <div className="flex flex-col items-center gap-4 px-8 text-center">
          <i className="ri-spy-line text-5xl" style={{ color: 'rgba(192,107,154,0.4)' }} />
          <p className="text-white font-bold" style={{ fontFamily: FONT_BODY }}>尚無配對資料</p>
          <p className="text-sm" style={{ color: 'rgba(192,107,154,0.4)', fontFamily: FONT_BODY }}>請裁判先在任務頁設置小主人配對</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center overflow-hidden select-none"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, #1e0620 0%, #06020f 100%)' }}
    >
      {/* BG dots */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(192,107,154,1) 1px, transparent 1px)', backgroundSize: '22px 22px', opacity: 0.04 }} />
      {/* Ambient orb */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[120px] opacity-15"
        style={{ background: 'radial-gradient(circle, rgba(192,107,154,0.6), transparent 65%)' }} />

      <FloatingHearts />

      {/* 角色標籤 */}
      {isJudge && ceremonyPhase !== 'done' && (
        <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(192,107,154,0.15)', border: '1px solid rgba(192,107,154,0.4)' }}>
          <i className="ri-user-star-line text-xs" style={{ color: PINK }} />
          <span className="text-[10px] font-black" style={{ color: PINK, fontFamily: FONT_BODY }}>裁判控制中</span>
        </div>
      )}

      {!isJudge && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ background: 'rgba(192,107,154,0.08)', border: '1px solid rgba(192,107,154,0.25)' }}>
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: PINK }} />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: PINK }} />
          </span>
          <span className="text-[10px] font-black" style={{ color: 'rgba(192,107,154,0.6)', fontFamily: FONT_BODY }}>LIVE · 裁判操作同步中</span>
        </div>
      )}

      {ceremonyPhase === 'intro' && (
        <IntroScreen pairCount={pairs.length} isJudge={isJudge} onAdvance={handleAdvance} />
      )}

      {ceremonyPhase === 'reveal' && currentPair && (
        <RevealScreen
          pair={currentPair}
          revealed={ceremonyRevealed}
          index={ceremonyIndex}
          total={pairs.length}
          isJudge={isJudge}
          onAdvance={handleAdvance}
        />
      )}

      {ceremonyPhase === 'done' && (
        <DoneScreen pairs={pairs} isJudge={isJudge} onClose={handleClose} />
      )}

      <style>{`
        @keyframes phaseIn {
          from { opacity: 0; transform: scale(0.85) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes cardReveal {
          from { opacity: 0; transform: scale(0.6) rotateY(90deg); filter: brightness(3); }
          60%  { filter: brightness(1.5); }
          to   { opacity: 1; transform: scale(1) rotateY(0deg); filter: brightness(1); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes mysteryPulse {
          0%, 100% { box-shadow: 0 0 16px rgba(192,107,154,0.3); }
          50%       { box-shadow: 0 0 32px rgba(192,107,154,0.6); }
        }
        @keyframes heartBeat {
          0%   { transform: scale(1); }
          30%  { transform: scale(1.6); }
          60%  { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
        @keyframes connectorGlow {
          from { opacity: 0; transform: scaleX(0); }
          to   { opacity: 1; transform: scaleX(1); }
        }
        @keyframes tapHint {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.04); }
        }
        @keyframes angelFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50%       { transform: translateY(-130px) rotate(18deg); opacity: 0.7; }
        }
        @keyframes angelGlow {
          0%, 100% { box-shadow: 0 0 50px rgba(192,107,154,0.5), 0 0 100px rgba(192,107,154,0.2); }
          50%       { box-shadow: 0 0 80px rgba(192,107,154,0.8), 0 0 160px rgba(192,107,154,0.4); }
        }
        @keyframes angelOrbit { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        ${Array.from({ length: 4 }, (_, i) => `
          @keyframes burstP${i} {
            from { transform: translate(-50%,-50%) scale(0); opacity: 1; }
            to   { transform: translate(calc(-50% + var(--bx)), calc(-50% + var(--by))) scale(0.35); opacity: 0; }
          }
        `).join('')}
      `}</style>
    </div>
  );
}
